/** Shared helpers for Close Job Card force-close when parts issue is pending. */

const BLOCKING_STATUSES = new Set(["pending", "partially issued", "not issued"]);
const COMPLETED_STATUSES = new Set(["issued", "approved", "completed"]);

function readIssueStatusFromRecord(record: unknown): string {
  if (!record || typeof record !== "object") return "";
  const row = record as Record<string, unknown>;
  const raw = row.issue_status ?? row.ISSUE_STATUS ?? row.issueStatus;
  return String(raw ?? "").trim();
}

export function getPartsIssueStatus(formData: Record<string, unknown> | undefined): string {
  if (!formData) return "";
  const direct = readIssueStatusFromRecord(formData);
  if (direct) return direct;

  const parentSelected =
    formData.parentid_selectedData ?? formData.parent_id_selectedData;
  return readIssueStatusFromRecord(parentSelected);
}

export function isPartsIssuePending(formData: Record<string, unknown> | undefined): boolean {
  return getPartsIssueStatus(formData).toLowerCase() === "pending";
}

export function isPartsIssueBlockingClose(
  formData: Record<string, unknown> | undefined
): boolean {
  const s = getPartsIssueStatus(formData).toLowerCase();
  if (COMPLETED_STATUSES.has(s)) return false;
  if (BLOCKING_STATUSES.has(s)) return true;
  return s === "";
}

export function isForceCloseWithoutPartsIssueChecked(
  formData: Record<string, unknown> | undefined
): boolean {
  if (!formData) return false;
  const raw =
    formData.chk_force_close_without_parts_issue ??
    formData.force_close_without_parts_issue;
  return (
    raw === true ||
    raw === 1 ||
    raw === "1" ||
    String(raw).toLowerCase() === "true"
  );
}

export function isCloseJobcardScreen(screenId: string | undefined): boolean {
  return screenId === "39" || screenId === "159";
}

export function isCloseJobcardSubmitBlocked(
  screenId: string | undefined,
  formData: Record<string, unknown> | undefined
): boolean {
  return (
    isCloseJobcardScreen(screenId) &&
    isPartsIssueBlockingClose(formData) &&
    !isForceCloseWithoutPartsIssueChecked(formData)
  );
}

export type ShowWhenConfig = {
  field?: string;
  value?: string;
  values?: string[];
  ignoreCase?: boolean;
};

export function matchesShowWhen(
  showWhen: ShowWhenConfig | undefined,
  formData: Record<string, unknown> | undefined
): boolean {
  if (!showWhen?.field) return true;
  const raw = String(formData?.[showWhen.field] ?? "").trim();
  const candidates =
    showWhen.values && showWhen.values.length > 0
      ? showWhen.values
      : showWhen.value !== undefined
        ? [showWhen.value]
        : [];
  if (candidates.length === 0) return true;
  const compare = (candidate: string) =>
    showWhen.ignoreCase
      ? raw.toLowerCase() === String(candidate ?? "").trim().toLowerCase()
      : raw === String(candidate ?? "").trim();
  return candidates.some(compare);
}
